import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingElement from "../loading-element/loading-element";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faNoteSticky } from "@fortawesome/free-solid-svg-icons";
import { Dialog_VM, DialogFrame, useDialog } from "./dialog-base";
import { ApprovalDialog } from "./useApprovalDialogByRole";
import { UserSearchAutoComplete } from "../Input/UserSearchAutoComplete";
import { User } from "../../../core/models/entities/User";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { Design } from "../../../core/models/entities/Design";

interface Props<T> {
  subject: string;
  onSkip: (entity: T) => Promise<void>;
  onOrder: (entity: T) => Promise<void>;
  onInitApproval: (entity: T, email?: string) => Promise<void>;
}

export function useApprovalDialogAdmin<T>({
  subject,
  onSkip,
  onOrder: onSkipAndOrder,
  onInitApproval,
}: Props<T>): ApprovalDialog<T> {
  const initApproval = useDialog();
  const userSelectionDialog = useDialog();
  const [loading, setLoading] = useState(false);
  const [entity, setEntity] = useState<T>();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  function handleSkip() {
    setLoading(true);
    onSkip(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  function handleStartApproval() {
    initApproval.closeDialog();
    userSelectionDialog.openDialog();
  }

  function handleUserSelectionApproval() {
    if (!selectedUser) return;

    //console.log("Making approval API call with email:", selectedUser.email);
    //console.log("Entity:", entity);

    setLoading(true);
    onInitApproval(entity!, selectedUser.email).finally(() => {
      setLoading(false);
      userSelectionDialog.closeDialog();
      setSelectedUser(null);
    });
  }

  function handleSkipAndOrder() {
    setLoading(true);
    onSkipAndOrder(entity!).finally(() => {
      setLoading(false);
      initApproval.closeDialog();
    });
  }

  // Get organization ID from entity (either logo or design)
  const getOrganizationId = (): number => {
    if (!entity) return 0;

    // Check if it's a logo with proper type guard
    const logoEntity = entity as unknown as BaseLogo;
    if (logoEntity.customer) {
      return logoEntity.customer.id;
    }

    // Check if it's a design with proper type guard
    const designEntity = entity as unknown as Design;
    if (designEntity.customer) {
      return designEntity.customer.id;
    }

    return 0;
  };

  const organizationId = getOrganizationId();

  return {
    component: (
      <>
        <InitiateApprovalDialog2
          loading={loading}
          onSkip={handleSkip}
          onInitiateApproval={handleStartApproval}
          onSkipAndOrder={handleSkipAndOrder}
          subject={subject}
          dialogVm={initApproval}
        />
        <UserSelectionDialog
          dialogVm={userSelectionDialog}
          loading={loading}
          selectedUser={selectedUser}
          onUserChange={setSelectedUser}
          onApprove={handleUserSelectionApproval}
          onCancel={() => {
            userSelectionDialog.closeDialog();
            setSelectedUser(null);
            initApproval.openDialog();
          }}
          organizationId={organizationId}
          subject={subject}
        />
      </>
    ),
    start: (entity) => {
      setEntity(entity);
      initApproval.openDialog();
    },
  };
}

interface InitiateApprovalDialog2_VM {
  onInitiateApproval: (e?: React.MouseEvent<HTMLElement>) => void;
  onSkipAndOrder: (e: React.MouseEvent<HTMLElement>) => void;
  onSkip: (e: React.MouseEvent<HTMLElement>) => void;
  subject: string;
  loading: boolean;
  dialogVm: Dialog_VM;
}

function InitiateApprovalDialog2({
  onInitiateApproval,
  subject,
  onSkipAndOrder,
  onSkip,
  loading,
  dialogVm,
}: InitiateApprovalDialog2_VM) {
  const { t } = useTranslation();

  function handleSkip(e: React.MouseEvent<HTMLElement>) {
    if (loading) {
      return;
    }
    onSkip(e);
  }

  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVm}>
      {loading && <LoadingElement transparent />}
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">{subject}</p>
        <p>{t("compDialogsInput.clickOptionToContinue")}</p>
        <div className={"flex justify-center space-x-4"}>
          <button
            className={
              "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
            }
            onClick={onSkip}
            disabled={loading}
          >
            <FontAwesomeIcon size={"lg"} icon={faCheck} />
            <span>{t("compDialogsInput.approveAndCommit")}</span>
          </button>
          <button
            className={
              "button-secondary w-48 uppercase align-items-center h-auto py-3 px-12 flex flex-col space-y-1"
            }
            onClick={onInitiateApproval}
            disabled={loading}
          >
            <FontAwesomeIcon size={"lg"} icon={faNoteSticky} />
            <span>{t("compDialogsInput.sendToApproval")}</span>
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}

interface UserSelectionDialogProps {
  dialogVm: Dialog_VM;
  loading: boolean;
  selectedUser: User | null;
  onUserChange: (user: User | null) => void;
  onApprove: () => void;
  onCancel: () => void;
  organizationId: number;
  subject: string;
}

function UserSelectionDialog({
  dialogVm,
  loading,
  selectedUser,
  onUserChange,
  onApprove,
  onCancel,
  organizationId,
  subject,
}: UserSelectionDialogProps) {
  const { t } = useTranslation();

  if (loading) return null;

  return (
    <DialogFrame dialogOption={dialogVm}>
      <div className={"flex flex-col gap-4 p-4"}>
        <p className="capitalize text-left text-base font-bold">
          {t("compDialogsInput.selectUserForApproval", { subject })}
        </p>
        <p>{t("compDialogsInput.chooseUserFromVendorOrg")}</p>

        <div className={"space-y-2"}>
          <label className="text-sm font-medium text-gray-700">
            {t("compDialogsInput.selectUserLabel")}
          </label>
          <UserSearchAutoComplete
            value={selectedUser}
            onChange={onUserChange}
            organizationId={organizationId}
            placeholder={t("compDialogsInput.searchUserByNameOrEmail")}
          />
        </div>

        <div className={"flex justify-center space-x-4 pt-4"}>
          <button
            className={
              "button-secondary w-32 uppercase align-items-center h-auto py-2 px-4"
            }
            onClick={onCancel}
            disabled={loading}
          >
            {t("cancel")}
          </button>
          <button
            className={
              "button-primary w-32 uppercase align-items-center h-auto py-2 px-4"
            }
            onClick={onApprove}
            disabled={loading || !selectedUser}
          >
            {t("compDialogsInput.sendApproval")}
          </button>
        </div>
      </div>
    </DialogFrame>
  );
}
